import { Button, FormHelperText } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { createRef } from "react";
import LoadingIndicator from "../../../components/common/LoadingIndicator";
import PlatformCheckbox from "./PlatformCheckbox";

// Action button styling for consistency
const buttonStyle = { p: 1, fontSize: "11px" };
const initialUncheckedOptionNames = [
  "Facebook Posts",
  "Facebook Profiles",
  "Linkedin Homepage",
  "Linkedin Hashtags",
  "Linkedin Apify"
];

/**
 * PlatformSelectionPanel component for managing platform selection in task configuration
 *
 * This component allows users to select which data collection platforms to include when creating
 * a new task. It filters available platforms based on the selected scraper class and provides
 * bulk selection controls for efficiency.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.platformOptionData - Available platform options with their configuration details
 *   Each platform object contains: {name, scraper_option, type}
 * @param {Object} props.platformOptionsRefs - Reference object to store refs for checkbox components
 * @param {Object} props.selectedTaskData - Current task configuration including scraperClass
 *
 * @returns {JSX.Element} UI for platform selection with checkboxes and control buttons
 */
const PlatformSelectionPanel = ({ platformOptionData, platformOptionsRefs, selectedTaskData }) => {
  // Get platforms filtered by current scraper class
  const getFilteredOptionData = () => {
    const scraperType = selectedTaskData.scraperClass.toLowerCase().replace(" ", "_");
    return platformOptionData.filter((option) => option.type === scraperType);
  };

  // Bulk update selections and checkbox refs
  const bulkUpdateSelection = (filterFn, selectState) => {
    // Then update checkbox refs
    setTimeout(() => {
      const refUpdateMethod = selectState ? "setChecked" : "setUnchecked";

      // If filterFn is not provided, update all refs
      // Otherwise, filter refs based on the provided filter function
      platformOptionsRefs.current
        .filter((ref) => {
          if (!filterFn) return true;
          const { scraperOption } = ref.current.getPlatformState();
          return filterFn(scraperOption);
        })
        .forEach((ref) => ref.current[refUpdateMethod]());
    }, 0);
  };

  const isGlassdoor = (input) => {
    // Extract the scraper option string based on input type
    const scraperOption = typeof input === "string" ? input : input.scraper_option || input.scraperOption || "";

    // Check if it's a Glassdoor option but not a review counter
    return scraperOption.includes("glassdoor") && !scraperOption.includes("review");
  };

  // Selection handler functions
  const selectAll = () => bulkUpdateSelection(null, true);
  const deselectAll = () => bulkUpdateSelection(null, false);
  const selectGlassdoor = () => bulkUpdateSelection(isGlassdoor, true);
  const deselectGlassdoor = () => bulkUpdateSelection(isGlassdoor, false);

  // Render platform checkboxes or loading indicator
  const renderCheckboxes = () => {
    if (!platformOptionData.length) {
      return <LoadingIndicator />;
    }

    const filteredOptions = getFilteredOptionData();

    // Create list of refs for each checkbox
    platformOptionsRefs.current = filteredOptions.map(() => createRef());

    return filteredOptions.map((option, index) => (
      <Grid size={{ sm: 8, lg: 4 }} key={option.name}>
        <PlatformCheckbox
          label={option.name}
          initialChecked={!initialUncheckedOptionNames.includes(option.name)}
          scraperOption={option.scraper_option}
          ref={platformOptionsRefs.current[index]}
        />
      </Grid>
    ));
  };

  return (
    <>
      <Grid container>{renderCheckboxes()}</Grid>
      <Grid container spacing={1} direction="row" sx={{ py: 2 }}>
        <Grid>
          <Button sx={buttonStyle} variant="outlined" size="small" onClick={selectAll}>
            Select All
          </Button>
        </Grid>
        <Grid>
          <Button sx={buttonStyle} variant="outlined" size="small" onClick={deselectAll}>
            Deselect All
          </Button>
        </Grid>
        <Grid>
          <Button sx={buttonStyle} variant="outlined" size="small" onClick={selectGlassdoor}>
            Select All Glassdoor
          </Button>
        </Grid>
        <Grid>
          <Button sx={buttonStyle} variant="outlined" size="small" onClick={deselectGlassdoor}>
            Deselect Glassdoor
          </Button>
        </Grid>
      </Grid>
      <FormHelperText>Checked platforms will be included</FormHelperText>
    </>
  );
};

export default PlatformSelectionPanel;
